/**
 * AI Admin Assistant - Conversational interface for database operations
 */

import React, { useState, useEffect, useRef } from 'react';
import { View, Text, TextInput, Pressable, StyleSheet, Modal, ActivityIndicator, Platform, ScrollView } from 'react-native';
import { MessageSquare, Send, X, CheckCircle2, AlertTriangle, Sparkles, HelpCircle } from '../lib/icons';
import { supabase } from '../lib/supabase';
import { parseCommand, needsClarification, isHighConfidence } from '../lib/ai-admin/commandParser';
import { executeCommand, validateAndPreviewCommand } from '../lib/ai-admin/actionExecutor';
import { generateConfirmation } from '../lib/ai-admin/confirmationHandler';
import { ChatMessage, ConfirmationData, ExecutionResult } from '../lib/ai-admin/types';
import { shadows } from '../lib/styles';

interface AIAdminAssistantProps {
  isSystemManager: boolean;
}

export default function AIAdminAssistant({ isSystemManager }: AIAdminAssistantProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputText, setInputText] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [pendingConfirmation, setPendingConfirmation] = useState<ConfirmationData | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const scrollViewRef = useRef<ScrollView>(null);
  const messageIdCounter = useRef(0);

  // Get current user ID
  useEffect(() => {
    const getUserId = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setUserId(user.id);
      }
    };
    getUserId();
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (scrollViewRef.current) {
      setTimeout(() => {
        scrollViewRef.current?.scrollToEnd({ animated: true });
      }, 100);
    }
  }, [messages]);

  // Don't render if not a system manager
  if (!isSystemManager) {
    return null;
  }

  const addMessage = (role: ChatMessage['role'], content: string, extras?: Partial<ChatMessage>) => {
    messageIdCounter.current += 1;
    const newMessage: ChatMessage = {
      id: `${Date.now()}-${messageIdCounter.current}`,
      role,
      content,
      timestamp: new Date(),
      ...extras,
    };
    setMessages(prev => [...prev, newMessage]);
  };

  const handleSendMessage = async () => {
    if (!inputText.trim() || !userId || isProcessing) return;

    const userMessage = inputText.trim();
    setInputText('');
    addMessage('user', userMessage);
    setIsProcessing(true);

    try {
      // Parse the command
      const parsedCommand = await parseCommand(userMessage);

      // Check if clarification is needed
      if (needsClarification(parsedCommand)) {
        addMessage('assistant', parsedCommand.clarificationNeeded || 'I need more information to proceed.');
        if (parsedCommand.suggestedQuestions && parsedCommand.suggestedQuestions.length > 0) {
          addMessage('system', `Suggestions:\n${parsedCommand.suggestedQuestions.map(q => `• ${q}`).join('\n')}`);
        }
        setIsProcessing(false);
        return;
      }

      // Check confidence
      if (!isHighConfidence(parsedCommand)) {
        addMessage('assistant', `I'm not very confident I understood that correctly (${Math.round(parsedCommand.confidence * 100)}% confidence). Could you rephrase or be more specific?`);
        setIsProcessing(false);
        return;
      }

      // Validate and get preview data
      const validation = await validateAndPreviewCommand(parsedCommand, userId);
      
      if (!validation.valid) {
        addMessage('assistant', `❌ ${validation.error || 'Command validation failed'}`);
        setIsProcessing(false);
        return;
      }

      // Generate confirmation
      const confirmation = generateConfirmation(parsedCommand, validation.previewData);
      
      // For help commands, execute immediately without confirmation
      if (parsedCommand.intent === 'help') {
        const result = await executeCommand(parsedCommand, userId);
        addMessage('assistant', result.message, { executionResult: result });
        setIsProcessing(false);
        return;
      }

      // Show confirmation for all other commands
      setPendingConfirmation(confirmation);
      addMessage('assistant', `I'll ${confirmation.preview}`, { confirmationData: confirmation });
      
      if (confirmation.warnings && confirmation.warnings.length > 0) {
        addMessage('system', `⚠️ ${confirmation.warnings.join('\n⚠️ ')}`);
      }

    } catch (error) {
      console.error('Error processing command:', error);
      addMessage('assistant', `❌ Error: ${error instanceof Error ? error.message : 'Unknown error occurred'}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirm = async () => {
    if (!pendingConfirmation || !userId) return;

    setIsProcessing(true);
    setPendingConfirmation(null);

    try {
      const result = await executeCommand(pendingConfirmation.command, userId);
      
      if (result.success) {
        addMessage('assistant', `✓ ${result.message}`, { executionResult: result });
        
        // Show detailed results for queries
        if (result.recordDetails && result.recordDetails.length > 0) {
          const preview = formatResultsPreview(result.recordDetails, pendingConfirmation.command.intent);
          addMessage('system', preview);
        }
      } else {
        addMessage('assistant', `❌ ${result.error || 'Operation failed'}`, { executionResult: result });
      }
    } catch (error) {
      console.error('Error executing command:', error);
      addMessage('assistant', `❌ Error: ${error instanceof Error ? error.message : 'Unknown error occurred'}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = () => {
    setPendingConfirmation(null);
    addMessage('assistant', 'Operation cancelled.');
  };

  const formatResultsPreview = (records: any[], intent: string): string => {
    if (records.length === 0) return 'No results found.';
    
    const preview = records.slice(0, 5).map((record, idx) => {
      if (intent.includes('service_request')) {
        return `${idx + 1}. ${record.title || 'Untitled'} - ${record.status} (${record.priority})`;
      } else if (intent.includes('admin_task')) {
        return `${idx + 1}. ${record.title || 'Untitled'} - ${record.status} (${record.priority})`;
      } else if (intent.includes('user')) {
        return `${idx + 1}. ${record.full_name || 'Unknown'} - ${record.lifecycle_status || 'N/A'}`;
      } else if (intent.includes('inventory')) {
        return `${idx + 1}. ${record.name || 'Untitled'} - ${record.category}`;
      }
      return `${idx + 1}. ${JSON.stringify(record).slice(0, 50)}...`;
    }).join('\n');

    const more = records.length > 5 ? `\n... and ${records.length - 5} more` : '';
    return `📋 Results:\n${preview}${more}`;
  };

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <Sparkles size={24} color="#000000" />
          <Text style={styles.headerTitle}>AI Admin Assistant</Text>
        </View>
      </View>

      {/* Welcome message */}
      {messages.length === 0 && (
        <View style={styles.welcomeContainer}>
          <MessageSquare size={48} color="#d1d5db" />
          <Text style={styles.welcomeTitle}>AI Admin Assistant</Text>
          <Text style={styles.welcomeText}>
            Ask me to help with service requests, tasks, users, or inventory.
          </Text>
          <View style={styles.examplesContainer}>
            <Text style={styles.examplesTitle}>Examples:</Text>
            <Text style={styles.exampleText}>• "Show me all urgent service requests"</Text>
            <Text style={styles.exampleText}>• "Mark task #123 as completed"</Text>
            <Text style={styles.exampleText}>• "What's the phone number for John?"</Text>
            <Pressable
              style={styles.helpButton}
              onPress={() => setInputText('help')}
            >
              <HelpCircle size={16} color="#6b7280" />
              <Text style={styles.helpButtonText}>See all commands</Text>
            </Pressable>
          </View>
        </View>
      )}

      {/* Messages */}
      <ScrollView
        ref={scrollViewRef}
        style={styles.messagesContainer}
        contentContainerStyle={styles.messagesContent}
        showsVerticalScrollIndicator={true}
      >
            {messages.map((message) => (
              <View key={message.id} style={styles.messageWrapper}>
                <View
                  style={[
                    styles.messageBubble,
                    message.role === 'user' && styles.userMessage,
                    message.role === 'assistant' && styles.assistantMessage,
                    message.role === 'system' && styles.systemMessage,
                  ]}
                >
                  <Text
                    style={[
                      styles.messageText,
                      message.role === 'user' && styles.userMessageText,
                    ]}
                  >
                    {message.content}
                  </Text>
                  
                  {/* Show confirmation buttons */}
                  {message.confirmationData && !message.executionResult && pendingConfirmation && (
                    <View style={styles.confirmationButtons}>
                      <Pressable
                        style={({ pressed }) => [
                          styles.confirmButton,
                          pressed && styles.confirmButtonPressed,
                          isProcessing && styles.confirmButtonDisabled,
                        ]}
                        onPress={handleConfirm}
                        disabled={isProcessing}
                      >
                        <CheckCircle2 size={16} color="#ffffff" />
                        <Text style={styles.confirmButtonText}>Confirm</Text>
                      </Pressable>
                      <Pressable
                        style={({ pressed }) => [
                          styles.cancelButton,
                          pressed && styles.cancelButtonPressed,
                          isProcessing && styles.confirmButtonDisabled,
                        ]}
                        onPress={handleCancel}
                        disabled={isProcessing}
                      >
                        <X size={16} color="#6b7280" />
                        <Text style={styles.cancelButtonText}>Cancel</Text>
                      </Pressable>
                    </View>
                  )}
                </View>
                <Text style={styles.messageTime}>
                  {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </Text>
              </View>
            ))}
            
        {isProcessing && (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="small" color="#9ca3af" />
            <Text style={styles.loadingText}>Processing...</Text>
          </View>
        )}
      </ScrollView>

      {/* Input */}
      <View style={styles.inputContainer}>
        <TextInput
          style={styles.input}
          value={inputText}
          onChangeText={setInputText}
          placeholder="Ask me anything..."
          placeholderTextColor="#9ca3af"
          multiline
          maxLength={500}
          editable={!isProcessing && !pendingConfirmation}
          onSubmitEditing={handleSendMessage}
        />
        <Pressable
          style={({ pressed }) => [
            styles.sendButton,
            pressed && styles.sendButtonPressed,
            (isProcessing || !inputText.trim() || pendingConfirmation) && styles.sendButtonDisabled,
          ]}
          onPress={handleSendMessage}
          disabled={isProcessing || !inputText.trim() || !!pendingConfirmation}
        >
          <Send size={20} color="#ffffff" />
        </Pressable>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#ffffff',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    paddingHorizontal: Platform.OS === 'web' ? 0 : 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  headerTitle: {
    fontSize: Platform.OS === 'web' ? 24 : 20,
    fontWeight: '600',
    color: '#000000',
  },
  welcomeContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  welcomeTitle: {
    fontSize: 24,
    fontWeight: '600',
    color: '#000000',
    marginTop: 16,
    marginBottom: 8,
  },
  welcomeText: {
    fontSize: 15,
    color: '#6b7280',
    textAlign: 'center',
    marginBottom: 24,
  },
  examplesContainer: {
    width: '100%',
    backgroundColor: '#f9fafb',
    borderRadius: 12,
    padding: 20,
  },
  examplesTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 12,
  },
  exampleText: {
    fontSize: 14,
    color: '#6b7280',
    marginBottom: 8,
  },
  helpButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 12,
    paddingVertical: 8,
  },
  helpButtonText: {
    fontSize: 14,
    color: '#6b7280',
    textDecorationLine: 'underline',
  },
  messagesContainer: {
    flex: 1,
  },
  messagesContent: {
    padding: Platform.OS === 'web' ? 0 : 16,
    paddingTop: 16,
    paddingBottom: 16,
    gap: 12,
  },
  messageWrapper: {
    gap: 4,
  },
  messageBubble: {
    borderRadius: 16,
    padding: 12,
    maxWidth: '85%',
  },
  userMessage: {
    alignSelf: 'flex-end',
    backgroundColor: '#000000',
  },
  assistantMessage: {
    alignSelf: 'flex-start',
    backgroundColor: '#f3f4f6',
  },
  systemMessage: {
    alignSelf: 'center',
    backgroundColor: '#fef3c7',
    maxWidth: '90%',
  },
  messageText: {
    fontSize: 15,
    color: '#374151',
    lineHeight: 20,
  },
  userMessageText: {
    color: '#ffffff',
  },
  messageTime: {
    fontSize: 11,
    color: '#9ca3af',
    alignSelf: 'flex-start',
    marginLeft: 12,
  },
  confirmationButtons: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 12,
  },
  confirmButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: '#000000',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  confirmButtonPressed: {
    opacity: 0.8,
  },
  confirmButtonDisabled: {
    opacity: 0.5,
  },
  confirmButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  cancelButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  cancelButtonPressed: {
    backgroundColor: '#f9fafb',
  },
  cancelButtonText: {
    color: '#6b7280',
    fontSize: 14,
    fontWeight: '600',
  },
  loadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 12,
  },
  loadingText: {
    fontSize: 14,
    color: '#9ca3af',
    fontStyle: 'italic',
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 12,
    paddingHorizontal: Platform.OS === 'web' ? 0 : 16,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    backgroundColor: '#ffffff',
  },
  input: {
    flex: 1,
    backgroundColor: '#f9fafb',
    borderRadius: 20,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 15,
    color: '#000000',
    maxHeight: 100,
  },
  sendButton: {
    backgroundColor: '#000000',
    borderRadius: 20,
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  sendButtonPressed: {
    opacity: 0.8,
  },
  sendButtonDisabled: {
    opacity: 0.3,
  },
});
